import React, { useMemo, useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { SLOT_TIME_FORMAT_SMALL } from '../../../../../constants/dates.constants';
import { getGroupedWeekSlots, groupSlotsByDate } from '../../../helpers/slots.helpers';
import useHorizontalDrag from '../../../hooks/useHorizontalDrag';
import { ISlot } from '../../../store/meeting/meeting.types';

import Day from './Day';
import Header from './Header';

const useStyles = makeStyles(({ breakpoints, palette, spacing }) => ({
  wrapper: {
    [breakpoints.down('sm')]: {
      marginBottom: spacing(20),
    },
  },
  body: {
    borderTop: `2px solid ${palette.grey.dark}`,
    [breakpoints.up('sm')]: {
      borderBottom: `2px solid ${palette.grey.dark}`,
      overflowX: 'auto',
      cursor: 'grab',
    },
  },
}));

export interface ICalendarProps {
  timezoneName: string;
  from: string;
  to: string;
  slots?: ISlot[];
  selectedSlots?: ISlot[];
  isLoading?: boolean;
  onChangeWeek: (range: { from: moment.Moment; to: moment.Moment }) => void;
  onChangeTimezone: (timezone: { timezoneName: string }) => void;
  onSelectSlot: (slot: ISlot, isSelected: boolean) => void;
}

const Calendar = ({
  timezoneName,
  from,
  to,
  slots = [],
  selectedSlots = [],
  isLoading = false,
  onChangeWeek,
  onChangeTimezone,
  onSelectSlot,
}: ICalendarProps): JSX.Element => {
  const classes = useStyles();

  const [slotsTimeFormat, setSlotsTimeFormat] = useState(SLOT_TIME_FORMAT_SMALL);

  const availableDays = useMemo(() => getGroupedWeekSlots(from, to, timezoneName)(slots), [
    from,
    to,
    slots,
    timezoneName,
  ]);

  const groupedSelectedSlots = useMemo(() => groupSlotsByDate(timezoneName)(selectedSlots), [
    selectedSlots,
    timezoneName,
  ]);

  const toggleSlotsTimeFormat = useCallback(format => setSlotsTimeFormat(format), []);

  const daysWrapper = useHorizontalDrag();

  return (
    <Box width={1} className={classes.wrapper}>
      <Header
        from={from}
        to={to}
        onChangeWeek={onChangeWeek}
        onChangeTimezone={onChangeTimezone}
        onToggleSlotsFormat={toggleSlotsTimeFormat}
        isLoading={isLoading}
      />
      <Box
        ref={daysWrapper}
        className={classes.body}
        minHeight={380}
        minWidth={1}
        pb={2}
        display="flex"
        flexDirection={['column', 'row']}
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        {Object.entries(availableDays).map(([day, daySlots]) => (
          <Day
            key={day}
            day={day}
            slots={daySlots}
            selectedSlots={groupedSelectedSlots[day] || []}
            timezoneName={timezoneName}
            isLoading={isLoading}
            slotsTimeFormat={slotsTimeFormat}
            onSelect={onSelectSlot}
          />
        ))}
      </Box>
    </Box>
  );
};

export default Calendar;
